<script lang="ts" setup>
import { Sort } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";

import {
  Checklist,
  ChecklistDefinitionCreateDto,
  ChecklistDefinitionUpdateDto,
  ItemDefinitionListDto,
  WorkflowDefinition,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

import { VueDraggableNext } from "vue-draggable-next";

import { useMyInfoStore } from "~/stores/MyInfoStore";
const myInfoStore = useMyInfoStore();

import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
const entityDefinitionStore = useEntityDefinitionStore();

const checklistDefinition = defineModel<Checklist>();
const editing = ref(true);
const sortingItems = ref(true);
const drag = ref(false);

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: Checklist): void;
  (e: "added", value: Checklist): void;
  (e: "deleted", value: Checklist): void;
}>();

// const publicItem = ref<boolean>();
// const copiedItemStore = useStorage("editing-copied-checklist", false, localStorage);

const itemsInChecklist = ref<string[]>([]);
const availableItems = ref<ItemDefinitionListDto[]>([]);
const associatedWorkflows = ref<WorkflowDefinition[]>();
const defaultWorkflowGuid = ref<string>();

const isUsable = computed(() => {
  return (
    checklistDefinition.value?.guid !== emptyGuid &&
    checklistDefinition.value?.guid !== undefined
  );
});

const saveItem = async () => {
  if (!checklistDefinition.value) {
    showError("Compliance item not found");
    return;
  }

  const guid = checklistDefinition.value.guid;
  if (!guid || guid === emptyGuid) {
    checklistDefinition.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
    const checklistToCreate: ChecklistDefinitionCreateDto = {
      ...checklistDefinition.value,
      defaultWorkflowGuid: defaultWorkflowGuid.value,
      itemsInChecklists:
        itemsInChecklist.value
          .map((i) => ({
            guid: i,
          }))
          .filter((i) => i !== undefined) ?? null,
    };

    if (!checklistDefinition.value.name) {
      showError("Checklist name is required");
      return;
    }
    if (!checklistDefinition.value.entityDefinitionGuid) {
      showError("Entity is required");
      return;
    }
    await apiClient.checklistDefinition
      .createChecklistDefinition(
        myInfoStore.currentSysOrgGuid,
        checklistToCreate,
      )
      .then((res) => {
        ElMessage.success("Saved");
        emit("added", res);

        editing.value = false;
        sortingItems.value = true;
        // handleCloseDrawer();
      })
      .catch(showError);
  } else {
    const checklistToUpdate: ChecklistDefinitionUpdateDto = {
      ...checklistDefinition.value,
      defaultWorkflowGuid: defaultWorkflowGuid.value,
      itemsInChecklists:
        itemsInChecklist.value
          .map((i, index) => ({
            guid: i,
            sortOrder: index,
          }))
          .filter((i) => i !== undefined) ?? null,
    };

    await apiClient.checklistDefinition
      // otherwise, we just update it
      .updateChecklistDefinition(
        guid,
        myInfoStore.currentSysOrgGuid,
        checklistToUpdate,
      )
      .then((res) => {
        editing.value = false;
        sortingItems.value = true;

        ElMessage.success("Saved");
        // copiedItemStore.value = false;
        emit("updated", res);
      })
      .catch(showError);
  }
};

const getAvailableItems = async () => {
  await apiClient.itemDefinition
    .getAllItemDefinitionDtos(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      availableItems.value = res.filter(
        (item) =>
          item.forEntityDefinitionGuid ===
            checklistDefinition.value?.entityDefinitionGuid ||
          item.forEntityDefinitionGuid === parentEntityDefinitionGuid.value,
      );
    })
    .catch(showError);
};

const getAvailableWorkflows = async () => {
  if (!checklistDefinition.value?.guid) {
    showError("Checklist guid not found");
    return;
  }

  await apiClient.checklistDefinition
    .getWorkflowsAssociatedWithChecklist(
      checklistDefinition.value.guid,
      myInfoStore.currentSysOrgGuid,
    )
    .then((res) => {
      associatedWorkflows.value = res;
    })
    .catch(showError);
};

onMounted(async () => {
  await Promise.all([myInfoStore.load(), entityDefinitionStore.load()]);
  if (checklistDefinition.value?.guid) {
    editing.value = false;
    sortingItems.value = true;
    await getAvailableWorkflows();
  } else {
    editing.value = true;
    sortingItems.value = false;
  }

  defaultWorkflowGuid.value =
    checklistDefinition.value?.defaultWorkflowGuid ?? undefined;

  await getAvailableItems();
  itemsInChecklist.value =
    checklistDefinition.value?.itemsInChecklists
      ?.map((i) => i.itemDefGuid)
      .filter((i) => i !== undefined) ?? [];

  console.log(availableItems.value);
});

const parentEntityDefinitionGuid = computed(() => {
  const filteredEntity = entityDefinitionStore.EntitiesDefinitionList.filter(
    (entity) => entity.guid === checklistDefinition.value?.entityDefinitionGuid,
  );
  return filteredEntity[0]?.parentEntityDefinitionGuid;
});

watch(
  () => checklistDefinition.value?.entityDefinitionGuid,
  async (newGuid, oldGuid) => {
    if (newGuid && newGuid !== oldGuid) {
      itemsInChecklist.value = [];
      await getAvailableItems();
    }
  },
);
</script>

<template>
  <div v-if="checklistDefinition" class="compliance-checklist-details">
    <div v-if="myInfoStore.hasSetupBasicRole" class="edit-buttons">
      <el-button v-if="!editing" type="success" @click="editing = true">
        Edit
      </el-button>

      <el-button v-if="editing" plain type="danger" @click="editing = false"
        >Cancel</el-button
      >
      <el-button v-if="editing" type="success" @click="saveItem"
        >Save</el-button
      >
    </div>
    <section class="section core">
      <table>
        <tbody>
          <tr>
            <td>Checklist Name</td>
            <td>
              <el-input v-if="editing" v-model="checklistDefinition.name" />
              <span v-else>{{ checklistDefinition.name }}</span>
            </td>
          </tr>
          <tr>
            <td>Entity Type</td>
            <td>
              <el-select
                v-if="editing"
                v-model="checklistDefinition.entityDefinitionGuid"
                filterable
              >
                <el-option
                  v-for="entity in entityDefinitionStore.EntitiesDefinitionList"
                  :key="entity.guid"
                  :label="entity.name ?? undefined"
                  :value="entity.guid"
                />
              </el-select>
              <span v-else>{{
                entityDefinitionStore.nameOfType(
                  checklistDefinition.entityDefinitionGuid,
                )
              }}</span>
            </td>
          </tr>
          <tr v-if="isUsable">
            <td>Default Workflow</td>
            <td>
              <div v-if="!editing">
                {{
                  associatedWorkflows?.find(
                    (w) => w.guid === defaultWorkflowGuid,
                  )?.name ??
                  defaultWorkflowGuid ??
                  "-"
                }}
              </div>
              <div v-else>
                <el-select
                  v-model="defaultWorkflowGuid"
                  placeholder="Select a workflow"
                  :empty-values="[null, undefined]"
                  :value-on-clear="undefined"
                  clearable
                  filterable
                >
                  <el-option
                    v-for="workflow in associatedWorkflows"
                    :key="workflow.guid"
                    :label="workflow.name ?? 'Unknown Workflow'"
                    :value="workflow.guid"
                  ></el-option>
                </el-select>
              </div>
            </td>
          </tr>
          <tr>
            <td>Items</td>
            <td>
              <div v-if="!editing">
                <div v-if="itemsInChecklist.length" class="item-tags">
                  <el-tag v-for="item in itemsInChecklist" :key="item">
                    {{
                      availableItems.find((i) => i.guid === item)?.name ||
                      "Unknown Item"
                    }}
                  </el-tag>
                </div>

                <span v-else>-</span>
              </div>
              <div v-else>
                <div
                  v-if="sortingItems"
                  class="sorting-items"
                  :style="drag ? 'cursor: grabbing' : 'cursor: grab'"
                >
                  <VueDraggableNext
                    class="dragArea list-group w-full"
                    :list="itemsInChecklist"
                    :force-fallback="true"
                    @choose="drag = true"
                    @unchoose="drag = false"
                  >
                    <div
                      v-for="(item, index) in itemsInChecklist"
                      :key="item"
                      class="list-group-item bg-gray-300 m-1 p-3 rounded-md text-center"
                      style="padding-bottom: 0.5em"
                    >
                      <span class="item-index">{{ index + 1 }}.</span>

                      <el-tag
                        size="large"
                        style="margin-left: 1em; width: fit-content"
                        class="status"
                      >
                        <div class="status-content">
                          <i
                            :class="`mdi mdi-drag-vertical`"
                            class="drag-icon"
                          ></i>
                          <span>
                            {{
                              availableItems.find((i) => i.guid === item)
                                ?.name || "Unknown Item"
                            }}
                          </span>
                        </div>
                      </el-tag>
                    </div>
                  </VueDraggableNext>

                  <el-button
                    type="info"
                    class="add"
                    @click="sortingItems = false"
                    >Edit Items</el-button
                  >
                </div>
                <el-transfer
                  v-else
                  v-model="itemsInChecklist"
                  :data="availableItems"
                  :titles="['Available', 'Added']"
                  :filterable="true"
                  :props="{
                    key: 'guid',
                    label: 'name',
                  }"
                  :filter-placeholder="'Search'"
                  :disabled="!editing"
                  target-order="push"
                >
                  <template #right-footer>
                    <el-button
                      :disabled="itemsInChecklist.length === 0"
                      type="info"
                      :icon="Sort"
                      @click="sortingItems = true"
                      >Sort</el-button
                    >
                  </template>
                </el-transfer>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<style lang="less">
.el-tabs,
.el-tabs__content,
.el-tab-pane {
  height: 100%;
}
.compliance-checklist-details {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, min-content) auto;
  grid-template-areas:
    "edit"
    "core"
    "capture"
    "copy";
  height: 100%;

  .edit-buttons {
    grid-area: edit;
    display: flex;
    .el-switch + .el-button {
      margin-left: 12px;
    }
  }

  .core {
    grid-area: core;

    .item-tags {
      .el-tag {
        display: flex;
        width: fit-content;
        user-select: none;
        margin: 2px 0;
      }
    }
    .el-transfer-panel__footer {
      .el-button {
        margin-left: 0.5em;
      }
    }

    .sorting-items {
      .list-group-item {
        display: grid;
        grid-template-columns: 1em auto;
        .status {
          padding-left: 0;
          .status-content {
            display: flex;
            align-items: center;
            .drag-icon {
              font-size: 1.5em;
            }
          }
        }
      }

      .actions {
        .arrow-button {
          margin: 0 2px;
          padding: 2px 6px;
          font-size: 12px;
        }
      }

      .add {
        margin-top: 1em;
      }
    }
  }

  .copy-btn {
    grid-area: copy;
    justify-self: end;
    align-self: end;
  }

  table {
    td {
      padding: 0.5em;
      border-bottom: 1px solid #ebeef5;
    }
  }
}
</style>
